import sanitize from 'sanitize-html';
import { Editor } from 'grapesjs';
import { TabsRenderer } from '@loyalty/editor-components';
import { getLocalizedProperty } from '@loyalty/shared-common';
import { EditorComponentDefinition } from '../types';
import { EditorContext } from '../../../services/editor-service';
import { TabsWidgetTraits, tabsWidgetTraitsSchema } from './TabsWidgetTraits';
import { Toolbar, buildParticipantContextFromGrape, getComponents } from '../../utils';

export const tabsComponentDefinition: EditorComponentDefinition = {
  id: 'tabs',
  icon: `<svg style="height: 100%" viewBox="0 0 53 39" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M14 10V6C14 3.23858 11.7614 1 9 1H6C3.23858 1 1 3.23858 1 6V10" stroke="#12384A" stroke-width="2"/>
<path d="M14 5C14 2.79086 15.7909 1 18 1H23C25.2091 1 27 2.79086 27 5V9H14V5Z" stroke="#12384A" stroke-width="2"/>
<path d="M27 5C27 2.79086 28.7909 1 31 1H36C38.2091 1 40 2.79086 40 5V9H27V5Z" stroke="#12384A" stroke-width="2"/>
<path d="M1 8V33C1 35.7614 3.23858 38 6 38H47C49.7614 38 52 35.7614 52 33V14C52 11.2386 49.7614 9 47 9H14" stroke="#12384A" stroke-width="2"/>
</svg>`,
  category: 'Basic',
  getLabel: ({ t }) => t('editor.component.tabs'),
  component: TabsGrapesComponent,
};

export const tabsContainerComponentDefinition: EditorComponentDefinition = {
  id: 'tab-container',
  icon: tabsComponentDefinition.icon,
  getLabel: () => '',
  traits: {
    schema: tabsWidgetTraitsSchema,
    form: TabsWidgetTraits,
  },
};

function TabsGrapesComponent(editor: Editor) {
  const i18n = editor.I18n;
  const context: EditorContext = editor.Config.context;

  editor.DomComponents.addType(tabsComponentDefinition.id, {
    model: {
      defaults: {
        draggable: true,
        droppable: false,
        copyable: false,
        removable: true,
        traits: [],
        components: [
          {
            type: tabsContainerComponentDefinition.id,
          },
          {
            type: 'tab-contents',
          },
        ],
      },
    },
  });

  editor.DomComponents.addType(tabsContainerComponentDefinition.id, {
    model: {
      defaults: {
        draggable: false,
        droppable: false,
        copyable: false,
        removable: false,
        traits: [],
        classes: ['tab-container'],
        attributes: {
          tabs: [{ contentId: null }, { contentId: null }, { contentId: null }],
          type: 'tabs',
        },
        components: [],
      },
      init() {
        // Legacy compatibility: extract tabs from old component structure
        // TODO: Remove this once the no legacy data are in the template
        const legacyTitleComponents = getComponents(this);
        if (legacyTitleComponents.length > 0) {
          const tabs = legacyTitleComponents.map((tab: any) => ({
            contentId: tab.attributes['aria-controls'] || '',
          }));

          // Extract labels from legacy title components for all languages
          const i18nAttributes = this.attributes.i18nAttributes || {};
          const languages: string[] = context.languages;

          // Build up the new i18nAttributes for each language
          const newI18nAttributes: Record<string, any> = { ...i18nAttributes };

          for (const lang of languages) {
            const tabLabels: Record<string, string> = {};

            legacyTitleComponents.forEach((tab: any, index: number) => {
              const contentId = tab.attributes['aria-controls'] || '';
              if (contentId) {
                tabLabels[contentId] = sanitize(
                  getLocalizedProperty(tab.components[0], 'content', lang) ||
                    tab.components[0].content ||
                    `Tab ${index + 1}`,
                  { allowedTags: [] },
                ).trim();
              }
            });

            newI18nAttributes[lang] = {
              ...newI18nAttributes[lang],
              tabLabels,
            };
          }
          this.addAttributes({ tabs });
          this.set('i18nAttributes', newI18nAttributes);
          this.set('components', []);
        }

        // Defer execution to next event loop tick
        // This ensures the tab-contents are initialized before syncing
        setTimeout(() => {
          this.syncWithTabContents();
          this.view.onRender();
        }, 0);
      },
      /**
       * Helper method to get the tab-contents or navigation-contents component from the parent.
       * Returns null if parent doesn't exist, is not of type 'tabs' or 'navigations', or if tab-contents is not found.
       */
      getTabContents() {
        const parent = this.parent();
        if (!parent || (parent.get('type') !== 'tabs' && parent.get('type') !== 'navigations')) {
          return null;
        }
        const tabContents = parent
          .components()
          .find(
            (c: any) => c.get('type') === 'tab-contents' || c.get('type') === 'navigation-contents',
          );
        return tabContents || null;
      },
      /**
       * Syncs the tab-container attributes.tabs with the tab-contents or navigation-contents children.
       * - Matches tabs to tab-content components by contentId first
       * - Removes tab-content components that don't match any tab
       * - Adds new tab-content components for tabs that don't have one
       * - Updates the contentId of each tab to match the corresponding tab-content ID
       */
      syncWithTabContents() {
        const tabContents = this.getTabContents();
        if (!tabContents) {
          return;
        }
        const tabs = this.getAttributes().tabs || [];
        const tabContentComponents = tabContents.components();
        // Build a map of existing tab-content components by their ID
        const tabContentsToSyncMap = new Map<string, any>();
        tabContentComponents.forEach((tabContent: any) => {
          tabContentsToSyncMap.set(tabContent.getId(), tabContent);
        });
        // Match tabs to tab-content components by contentId and update tabs
        const updatedTabs = tabs.map((tab: { contentId: string | null }) => {
          // If tab has a contentId that exists, keep it
          if (tab.contentId && tabContentsToSyncMap.has(tab.contentId)) {
            tabContentsToSyncMap.delete(tab.contentId); // Mark as synced
            return tab;
          }
          // Tab doesn't have a matching contentId, will create a new one
          return { contentId: null };
        });
        // Remove tab-content components that don't match any tab
        tabContentsToSyncMap.forEach((tabContent: any) => {
          tabContent.remove();
        });
        // Add new tab-content components for tabs that don't have one
        updatedTabs.forEach((tab: { contentId: string | null }, index: number) => {
          if (!tab.contentId) {
            const newTabContents = tabContents.append({ type: 'tab-content' });
            updatedTabs[index] = {
              contentId: newTabContents[0].getId(),
            };
          }
        });
        this.addAttributes({ tabs: updatedTabs });
      },
      /**
       * Syncs the i18nAttributes.tabLabels with the tabs.contentId.
       * - Removes labels for tabs that don't have a contentId
       */
      syncI18nAttributesAcrossLocales() {
        const languages: string[] = context.languages || [];
        const tabs = this.getAttributes().tabs || [];
        const i18nAttributes = this.attributes.i18nAttributes || {};

        // Get the set of valid contentIds
        const validContentIds = new Set(
          tabs.map((tab: { contentId: string | null }) => tab.contentId).filter(Boolean),
        );

        const updatedI18nAttributes = { ...i18nAttributes };

        for (const lang of languages) {
          const tabLabels = updatedI18nAttributes[lang]?.tabLabels || {};
          const cleanedTabLabels: Record<string, string> = {};

          // Only keep labels for tabs that still exist
          for (const contentId of Object.keys(tabLabels)) {
            if (validContentIds.has(contentId)) {
              cleanedTabLabels[contentId] = tabLabels[contentId];
            }
          }
          updatedI18nAttributes[lang] = {
            ...updatedI18nAttributes[lang],
            tabLabels: cleanedTabLabels,
          };
        }

        this.set('i18nAttributes', updatedI18nAttributes);
      },
      /**
       * Adds a new tab-content/navigation-content component and returns the id of the new component.
       */
      addTabContent() {
        const tabContents = this.getTabContents();
        if (!tabContents) {
          return;
        }
        const parent = this.parent();
        const newTabContents = tabContents.append({
          type: parent.get('type') === 'tabs' ? 'tab-content' : 'navigation-content',
        });
        // The grapesjs component was created via append, with a newly generated id
        return newTabContents[0].getId();
      },
      /**
       * Removes a tab-content/navigation-content component by its id.
       */
      removeTabContent(id: string) {
        const tabContents = this.getTabContents();
        if (!tabContents) {
          return;
        }
        const tabContent = tabContents.components().find((c: any) => c.getId() === id);
        if (!tabContent) {
          return;
        }
        tabContent.remove();
      },
      initToolbar: Toolbar,
      toHTML(): string {
        this.syncI18nAttributesAcrossLocales();
        return TabsRenderer(
          {
            state: context.widgetState,
            options: {
              id: this.getId(),
              attributes: this.getAttributes(),
              i18nAttributes: this.attributes.i18nAttributes || {},
              components: getComponents(this),
            },
            context: buildParticipantContextFromGrape(i18n),
          },
          editor.Canvas.getDocument(),
        );
      },
    },
    view: {
      onRender() {
        const { el, model } = this;
        el.innerHTML = model.toHTML();
      },
      addTabContent() {
        return this.model.addTabContent();
      },
      removeTabContent(id: string) {
        return this.model.removeTabContent(id);
      },
    },
  });

  editor.DomComponents.addType('tab-contents', {
    model: {
      defaults: {
        draggable: false,
        droppable: false,
        copyable: false,
        removable: false,
        classes: ['tab-contents'],
        traits: [],
      },
    },
  });

  editor.DomComponents.addType('tab-content', {
    model: {
      defaults: {
        draggable: false,
        droppable: false,
        copyable: false,
        removable: false,
        attributes: {
          role: 'tabpanel',
          hidden: true,
        },
        traits: [],
        classes: ['tab-content'],
        components: [
          {
            type: 'column',
            draggable: false,
            droppable: true,
            copyable: false,
            removable: false,
            classes: ['qlfeditor-column', 'tab-content-column'],
          },
        ],
      },
      init() {
        // Ensure the id is set in the template
        this.addAttributes({ id: this.getId() });
      },
    },
  });
}
